import React, { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from '@sravni/react-design-system';
import { useDispatch } from 'react-redux';
import { ValidationError } from 'yup';
import {
    amountTransform,
    Col,
    onlyNumbersOutput,
    PhoneInput,
    Row,
    Rows,
    SelectInput,
    TextInput,
} from '../../../../../../../landing.common';
import { FORM_FIELDS, MASKS, PAGE_CONTEXT, PLACEHOLDERS } from '../../../../../../../../constants';
import {
    additionalPhoneOwnerOptions,
    carTypeOptions,
    dependentsOptions,
    educationOptions,
    familyStatusOptions,
    propertyTypeOptions,
} from '../../../../../../../../helpers/form.new/sources';
import { FullNameSuggest } from '../../../../../../../form/ui/suggest/full_name';
import { EventType } from '../../../../../../../../helpers/analytics';
import { ADDITIONAL_PHONE_OWNER_DEPS } from '../../../../../../../../constants/field.dependencies';
import { useFormData, useFormStore } from '../../../../../../../../store/selectors/formStore';
import { setPageContext } from '../../../../../../../../store/reducers/formStore';
import { useFormOnBlur } from '../../../../../../../form/hooks/formHooks';
import { useAnalytics } from '../../../../../../../hooks/analytics';
import { schema } from './schema';
import { NotRequiredAlert } from './components/not.required.alert';

type Props = {
    onNext: () => void;
    onAutoValidationError: (e: ValidationError) => void;
};

/** Дополнительные данные */
export const Step4: React.FC<Props> = (props) => {
    const { onNext, onAutoValidationError } = props;
    const dispatch = useDispatch();
    const formData = useFormData();
    const { holdOn, autoRun } = useFormStore();
    const form = useForm({ resolver: yupResolver(schema()), mode: 'onSubmit', reValidateMode: 'onBlur' });
    const { pushEvent } = useAnalytics();

    const {
        control,
        handleSubmit,
        formState: { errors, dirtyFields: _, isValidating },
        getValues,
        setValue,
        reset,
        watch,
    } = form;

    const inProcess = isValidating || holdOn;

    useEffect(() => {
        reset(formData, { keepErrors: true, keepDirtyValues: true });
    }, [formData, reset]);

    useEffect(() => {
        dispatch(setPageContext(PAGE_CONTEXT.STEP_4));
        if (autoRun) {
            schema().validate(formData).then(onSubmit).catch(onAutoValidationError);
        }
    }, [autoRun]);

    const emitFourthStepAnalytics = () => {
        const [education, family, car] = getValues([FORM_FIELDS.EDUCATION, FORM_FIELDS.FAMILY_STATUS, FORM_FIELDS.CAR]);
        const educationChosen = educationOptions.find((item) => item.value === education);
        const familyChosen = familyStatusOptions.find((item) => item.value === family);
        const carChosen = carTypeOptions.find((item) => item.value === car);

        pushEvent({
            event: EventType.mainEvent,
            eventAction: 'Шаг 4|Доп. информация',
            eventLabel: `Образование - ${educationChosen ? educationChosen.label : ''}|Семейное положение - ${
                familyChosen ? familyChosen.label : ''
            }|Автомобиль - ${carChosen ? carChosen.label : ''}`,
        });
    };

    const onSubmit = async () => {
        emitFourthStepAnalytics();
        onNext();
    };

    const additionalPhoneOwner = watch(FORM_FIELDS.ADDITIONAL_PHONE_OWNER);

    const additionalPhoneFields = useMemo(
        () => ADDITIONAL_PHONE_OWNER_DEPS[additionalPhoneOwner] || [],
        [additionalPhoneOwner],
    );

    const onSelectFullName: React.ComponentProps<typeof FullNameSuggest>['onSelect'] = (option) => {
        setValue(FORM_FIELDS.ADDITIONAL_PHONE_OWNER_FIO, option.result);
    };

    const formOnBlur = useFormOnBlur(form, false);

    return (
        <form onSubmit={handleSubmit(onSubmit)} onBlur={formOnBlur}>
            <Rows>
                <Row>
                    <Col md-6>
                        <Controller
                            control={control}
                            name={FORM_FIELDS.EDUCATION}
                            render={({ field }) => (
                                <SelectInput
                                    {...field}
                                    label="Образование"
                                    withCrossIcon={false}
                                    options={educationOptions}
                                    errorMessage={errors[FORM_FIELDS.EDUCATION]?.message}
                                />
                            )}
                        />
                    </Col>
                    <Col md-6>
                        <Controller
                            control={control}
                            name={FORM_FIELDS.FAMILY_STATUS}
                            render={({ field }) => (
                                <SelectInput
                                    {...field}
                                    label="Семейное положение"
                                    withCrossIcon={false}
                                    options={familyStatusOptions}
                                    errorMessage={errors[FORM_FIELDS.FAMILY_STATUS]?.message}
                                />
                            )}
                        />
                    </Col>
                </Row>

                <Row>
                    <Col md-6>
                        <Controller
                            control={control}
                            name={FORM_FIELDS.ADDITIONAL_PHONE}
                            render={({ field }) => (
                                <PhoneInput
                                    {...field}
                                    label="Дополнительный телефон"
                                    onChange={onlyNumbersOutput(field)}
                                    errorMessage={errors[FORM_FIELDS.ADDITIONAL_PHONE]?.message}
                                />
                            )}
                        />
                    </Col>
                    <Col md-6>
                        <Controller
                            control={control}
                            name={FORM_FIELDS.ADDITIONAL_PHONE_OWNER}
                            render={({ field }) => (
                                <SelectInput
                                    {...field}
                                    label="Владелец телефона"
                                    withCrossIcon={false}
                                    options={additionalPhoneOwnerOptions}
                                    errorMessage={errors[FORM_FIELDS.ADDITIONAL_PHONE_OWNER]?.message}
                                />
                            )}
                        />
                    </Col>
                </Row>

                {additionalPhoneFields.includes(FORM_FIELDS.ADDITIONAL_PHONE_OWNER_FIO) && (
                    <Row>
                        <Col>
                            <Controller
                                control={control}
                                name={FORM_FIELDS.ADDITIONAL_PHONE_OWNER_FIO}
                                render={({ field: { onBlur, ...field } }) => (
                                    <FullNameSuggest
                                        input={{
                                            ...field,
                                            label: 'ФИО владельца доп. телефона',
                                            errorMessage: errors[FORM_FIELDS.ADDITIONAL_PHONE_OWNER_FIO]?.message,
                                        }}
                                        onSelect={onSelectFullName}
                                        onChange={field.onChange}
                                        onBlur={onBlur}
                                    />
                                )}
                            />
                        </Col>
                    </Row>
                )}

                <Row>
                    <Col>
                        <NotRequiredAlert />
                    </Col>
                </Row>

                <Row>
                    <Col md-6>
                        <Controller
                            control={control}
                            name={FORM_FIELDS.NUMBER_OF_DEPENDENTS}
                            render={({ field }) => (
                                <SelectInput
                                    {...field}
                                    label="Дети до 18 лет"
                                    options={dependentsOptions}
                                    errorMessage={errors[FORM_FIELDS.NUMBER_OF_DEPENDENTS]?.message}
                                />
                            )}
                        />
                    </Col>
                    <Col md-6>
                        <Controller
                            control={control}
                            name={FORM_FIELDS.SNILS}
                            render={({ field }) => (
                                <TextInput
                                    {...field}
                                    label="СНИЛС"
                                    mask={MASKS.SNILS}
                                    placeholder={PLACEHOLDERS.SNILS}
                                    onChange={onlyNumbersOutput(field)}
                                    errorMessage={errors[FORM_FIELDS.SNILS]?.message}
                                />
                            )}
                        />
                    </Col>
                </Row>

                <Row>
                    <Col md-6>
                        <Controller
                            control={control}
                            name={FORM_FIELDS.ADDITIONAL_INCOME}
                            render={({ field }) => (
                                <TextInput
                                    {...field}
                                    label="Дополнительный доход"
                                    value={amountTransform.input(field)}
                                    onChange={amountTransform.output(field)}
                                    errorMessage={errors[FORM_FIELDS.ADDITIONAL_INCOME]?.message}
                                />
                            )}
                        />
                    </Col>
                    <Col md-6>
                        <Controller
                            control={control}
                            name={FORM_FIELDS.PROPERTY_TYPE}
                            render={({ field }) => (
                                <SelectInput
                                    {...field}
                                    label="Наличие недвижимости"
                                    options={propertyTypeOptions}
                                    errorMessage={errors[FORM_FIELDS.PROPERTY_TYPE]?.message}
                                />
                            )}
                        />
                    </Col>
                </Row>

                <Row>
                    <Col>
                        <Controller
                            control={control}
                            name={FORM_FIELDS.CAR}
                            render={({ field }) => (
                                <SelectInput
                                    {...field}
                                    label="Наличие автомобиля"
                                    options={carTypeOptions}
                                    errorMessage={errors[FORM_FIELDS.CAR]?.message}
                                />
                            )}
                        />
                    </Col>
                </Row>

                <Row>
                    <Col md-6 />
                    <Col md-6>
                        <Button loading={inProcess} block variant="primary" type="submit">
                            Посмотреть предложения
                        </Button>
                    </Col>
                </Row>
            </Rows>
        </form>
    );
};
